import * as React from "react";
import {
  failure,
  fold,
  initial,
  pending,
  RemoteData,
  success,
} from "@devexperts/remote-data-ts";
import { Box, CircularProgress } from "@material-ui/core";
import { takeRight } from "lodash";
import moment from "moment";
import { withRouter } from "react-router-dom";

import { CustomizedXAxisTick, TrackList } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { Desktop, Mobile } from "containers/Responsive";
import {
  AbstractRootComponent,
  RootComponentProps,
} from "containers/Responsive/AbstractRootComponent";
import { BackButtonLogoHeader } from "containers/Responsive/BackButtonLogoHeader";
import { Chart } from "containers/ValuationResults/RevenueChart";
import { SourceBreakdown } from "containers/ValuationResults/SourceBreakdown";
import {
  getReportOnTopSources,
  parseChartDataIntoTuples,
} from "containers/ValuationResults/valuationReportPostProcessor";
import { setYAxisUpperDomain } from "lib/chart";
import { formatCurrencyAmount, formatHumanDate } from "lib/format";
import {
  formatMoney,
  getTermNiceName,
  goToAuctions404,
  goToOrderbookBuyerDashboard,
  goToVP,
  singularOrPlural,
} from "lib/helpers";
import { getInstantOffer } from "services/offers";
import { InstantOffer } from "services/offers/types";

import { KeyValueTable } from "./KeyValueTable";

interface State {
  instantOffer: RemoteData<string, InstantOffer>;
}

export class InstantOfferDetail extends AbstractRootComponent<State> {
  protected offerId: number;
  public constructor(props: RootComponentProps) {
    super(props);
    this.offerId = this.props.match.params.orderFillId;

    this.state = {
      instantOffer: initial as RemoteData<string, InstantOffer>,
    };
  }

  public body() {
    const { instantOffer } = this.state;

    return fold<string, InstantOffer, React.ReactElement>(
      () => <RemoteDataLoading />,
      () => <RemoteDataLoading />,
      err => <RemoteDataError reason={err} />,
      instantOffer => {
        return <InstantOfferBody instantOffer={instantOffer} />;
      },
    )(instantOffer);
  }

  public header() {
    return (
      <>
        <div id="instant-offer-container">
          <BackButtonLogoHeader
            backText="Back to Buyer Dashboard"
            onClick={() => {
              const currentUrl = new URL(window.location.toLocaleString());
              if (currentUrl.pathname.includes("/preview")) {
                const valuationId = window.location.hash.split("valuation=")[1];
                return goToVP(
                  undefined,
                  false,
                  `valuation-analysis/${valuationId}/#action=deal`,
                );
              }
              return goToOrderbookBuyerDashboard();
            }}
          />
        </div>
      </>
    );
  }

  public componentDidMount() {
    this.setState(
      {
        instantOffer: pending,
      },
      async () => {
        try {
          const instantOffer = await getInstantOffer(this.offerId);
          this.setState({
            instantOffer: success(JSON.parse(JSON.stringify(instantOffer))),
          });
        } catch (error: any) {
          goToAuctions404();
          this.setState({
            instantOffer: failure("error:" + JSON.stringify(error)),
          });
        }
      },
    );
  }
}

interface Props {
  instantOffer: InstantOffer;
}

function InstantOfferBody(props: Props) {
  const { instantOffer } = props;
  const chosenResult = instantOffer.valuation.chosen_result;
  const earningsPeriodString = chosenResult.first_earnings_date
    ? `Earnings Since ${moment(chosenResult.first_earnings_date).format(
        "YYYY",
      )}`
    : "Lifetime Earnings";

  const title = "Order Fill";

  const formatedStateDate = formatHumanDate(instantOffer.state_date);
  const earningsAmountList = takeRight(
    parseChartDataIntoTuples(chosenResult.earnings_by_region),
    6,
  ).map(z => z.amount);

  return (
    <>
      <div id="instant-offer-container">
        <div className="instant-offer-title">
          <h1>{title}</h1>
          <label>
            Deal ID: <b>{instantOffer.id}</b>
          </label>
        </div>
        <div className="top-report">
          <div className="left-container">
            <KeyValueTable
              className="key-value-table-instant-offer"
              title="Order Information"
              rows={[
                ["Fill Date", formatedStateDate],
                [
                  "Amount Filled",
                  formatCurrencyAmount("USD", instantOffer.amount),
                ],
                ["Multiple", formatMoney(instantOffer.multiple, 2)],
                ["Investment Term", getTermNiceName(instantOffer.term)],
              ]}
            />
            <KeyValueTable
              className="key-value-table-instant-offer"
              title="Catalog Information"
              rows={[
                [
                  "Last 12 Months' Earnings",
                  "$" + formatMoney(chosenResult.ltm),
                ],
                [
                  earningsPeriodString,
                  "$" + formatMoney(chosenResult.lifetime_amount),
                ],
                [
                  "Adjusted Trend",
                  chosenResult.adj_trend !== null
                    ? (chosenResult.adj_trend * 100).toFixed(2) + "%"
                    : EMPTY_VALUE_PLACEHOLDER,
                ],
                ["Dollar Age", formatMoney(chosenResult.dollar_age, 2)],
                [
                  singularOrPlural(
                    "Track",
                    "Tracks",
                    chosenResult.track_list.length,
                  ) + " included",
                  chosenResult.track_list.length.toString(),
                ],
              ]}
            />
          </div>
          <div className="right-container">
            <div className="ltm-source-breakdown">
              <SourceBreakdown
                data={getReportOnTopSources(
                  chosenResult.top_sources.map(s => ({
                    ...s,
                    earnings: Number(s.earnings),
                  })),
                )}
                totalValue={chosenResult.top_sources.reduce(
                  (a, b) => a + Number(b.earnings),
                  0,
                )}
                colorList={[
                  "#333333",
                  "#4F4F4F",
                  "#828282",
                  "#BDBDBD",
                  "#E0E0E0",
                ]}
                title="Earning Source Breakdown"
                pieChartHeight={170}
              />
            </div>
            <div className="instant-offer-recent-earnings">
              <h2>Recent Earnings</h2>
              <Chart
                data={takeRight(
                  parseChartDataIntoTuples(chosenResult.earnings_by_region),
                  6,
                )}
                padding={0}
                height={160}
                marginHorizontal={8}
                marginVertical={10}
                isMobile
                strokeColor="#333333"
                fillColor="rgba(51, 51, 51, 0.1)"
                shouldHideXLabel={false}
                xLabelPosition="insideBottom"
                secondXAxisCustomTicks={<CustomizedXAxisTick />}
                customYAxisDomain={[
                  0,
                  `dataMax + ${setYAxisUpperDomain(earningsAmountList)}`,
                ]}
                customYAxisScale="linear"
                distributionFrequency={
                  instantOffer.valuation.distribution_frequency
                }
              />
            </div>
          </div>
        </div>
        <div className="bottom-report">
          <Desktop>
            <TrackList
              title="Included Track List"
              containerClass="included-track-list"
              tracks={chosenResult.track_list}
              collapsable
              platformContainerClass="instant-offer-track-list"
              maximumRows={30}
            />
          </Desktop>
          <Mobile>
            <TrackList
              title="Included Track List"
              containerClass="included-track-list"
              tracks={chosenResult.track_list}
              collapsable
              platformContainerClass="instant-offer-track-list"
            />
          </Mobile>
        </div>
      </div>
    </>
  );
}

function RemoteDataLoading() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="50vh"
    >
      <CircularProgress size={50} />
    </Box>
  );
}

function RemoteDataError(props: { reason?: string }) {
  return <>Error: {props.reason}</>;
}

const InstantOfferDetailWithRouter = withRouter(InstantOfferDetail);

export default InstantOfferDetailWithRouter;
